import { useState, useEffect } from "react";
import { Calendar, ChevronRight } from "lucide-react";
import { DayPlan, DayPlanItem } from "../lib/supabase";
import { supabase } from "../lib/supabase";
import { formatTime } from "../lib/scheduler";

interface DaySummary {
  plan: DayPlan;
  items: DayPlanItem[];
  taken: number;
  missed: number;
  total: number;
}

export function History() {
  const [history, setHistory] = useState<DaySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<DaySummary | null>(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: plans } = await supabase
        .from("day_plans")
        .select("*")
        .gte("wake_time", thirtyDaysAgo.toISOString())
        .order("wake_time", { ascending: false });

      if (!plans) {
        setHistory([]);
        return;
      }

      const summaries: DaySummary[] = [];

      for (const plan of plans) {
        const { data: items } = await supabase
          .from("day_plan_items")
          .select("*")
          .eq("day_plan_id", plan.id)
          .order("planned_time");

        if (items) {
          const taken = items.filter((i) => i.status === "TAKEN").length;
          const missed = items.filter((i) => i.status === "MISSED").length;

          summaries.push({
            plan,
            items,
            taken,
            missed,
            total: items.length,
          });
        }
      }

      setHistory(summaries);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  };

  const getCompletionRate = (summary: DaySummary) => {
    if (summary.total === 0) return 0;
    return Math.round((summary.taken / summary.total) * 100);
  };

  if (selectedDay) {
    return (
      <div className="max-w-4xl mx-auto p-4 safe-area-x">
        <button
          onClick={() => setSelectedDay(null)}
          className="mb-4 text-warm-secondary hover:text-warm-dark font-medium transition-colors"
        >
          ← Back to history
        </button>

        <div className="bg-warm-card rounded-xl border border-warm p-6 mb-4 warm-shadow">
          <h2 className="text-xl font-bold text-warm-dark mb-2">
            {formatDate(selectedDay.plan.wake_time)}
          </h2>
          <p className="text-gray-700">
            Woke up at {formatTime(new Date(selectedDay.plan.wake_time))}
          </p>
          <div className="flex gap-4 mt-4 text-sm">
            <span className="text-warm-secondary font-medium">
              {selectedDay.taken} taken
            </span>
            <span className="text-red-600 font-medium">
              {selectedDay.missed} missed
            </span>
            <span className="text-gray-600">
              {selectedDay.total - selectedDay.taken - selectedDay.missed}{" "}
              pending
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {selectedDay.items.map((item) => (
            <div
              key={item.id}
              className="bg-warm-glass rounded-lg border border-warm p-4 warm-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-warm-dark">
                    {item.preset_snapshot.name}
                  </h3>
                  <p className="text-sm text-gray-700 mt-1">
                    Planned: {formatTime(new Date(item.planned_time))}
                  </p>
                  {item.actual_time && (
                    <p className="text-sm text-gray-700">
                      Actual: {formatTime(new Date(item.actual_time))}
                    </p>
                  )}
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    item.status === "TAKEN"
                      ? "bg-gradient-to-r from-amber-100 to-orange-100 text-warm-secondary"
                      : item.status === "MISSED"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {item.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-600">Loading history...</div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 safe-area-x">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-warm-dark mb-2">History</h1>
        <p className="text-gray-700">
          Your supplement adherence over the last 30 days
        </p>
      </div>

      {history.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">
            No history yet. Start by creating your first daily plan!
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((summary) => {
            const completionRate = getCompletionRate(summary);

            return (
              <button
                key={summary.plan.id}
                onClick={() => setSelectedDay(summary)}
                className="w-full bg-warm-card rounded-xl border border-warm p-4 hover:shadow-lg hover:warm-glow transition-all text-left warm-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="text-warm-primary" size={20} />
                      <span className="font-semibold text-warm-dark">
                        {formatDate(summary.plan.wake_time)}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-warm-secondary font-medium">
                        {summary.taken}/{summary.total} taken
                      </span>
                      {summary.missed > 0 && (
                        <span className="text-red-600">
                          {summary.missed} missed
                        </span>
                      )}
                    </div>

                    <div className="mt-3 bg-gradient-to-r from-amber-100 to-orange-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-warm-primary h-full transition-all"
                        style={{ width: `${completionRate}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-700 mt-1">
                      {completionRate}% completion
                    </p>
                  </div>

                  <ChevronRight className="text-warm-primary" size={20} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
